import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Drawer,
  AppBar,
  Button,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import COLORS from '../../../resources/style/Themes';
import { ALL_USERS_PATH, ALL_USERS_SLUG } from './allUsersConstants';

const ALL_USERS_SELECTED_BACKGROUND = '#f0f0f0';


/**
 * @typedef {object} GroupManagementRouteBarProps
 * @prop {Group[]} allGroups
 * @prop {(groupName: string) => void} setActiveGroup
 * @prop {(isActive: boolean) => void} setShowCreateNewGroupModal
 * @prop {string} activeGroup
 * @prop {number} [allUsersCount]
 */

/**
 *
 * @param {GroupManagementRouteBarProps} props
 * @returns
 */
const GroupManagementRouteBar = ({
  allGroups,
  setActiveGroup,
  setShowCreateNewGroupModal,
  activeGroup,
  allUsersCount = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const { t } = useTranslation();

  /**
   *
   * @param {string} groupName
   */
  const handleGroupChange = (groupName) => {
    setActiveGroup(groupName);
    if (groupName === ALL_USERS_SLUG) {
      history.push(ALL_USERS_PATH);
      return;
    }
    history.push(`/UserManagement/${encodeURIComponent(groupName)}`);
  };

  const renderMobileView = () => (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: COLORS.WHITE,
        boxShadow: 0,
        top: 64,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 2 }}>
        <FormControl fullWidth size="small">
          <Select
            value={activeGroup}
            onChange={(e) => handleGroupChange(e.target.value)}
            sx={{
              backgroundColor: COLORS.WHITE,
              '& .MuiSelect-select': {
                py: 1.5,
              },
            }}
          >
            <MenuItem value={ALL_USERS_SLUG}>
              <Typography>
                {t(StringKeys.ALL_USERS)} (
                {t(StringKeys.X_USERS, { count: allUsersCount })})
              </Typography>
            </MenuItem>
            {allGroups?.map((group) => (
              <MenuItem key={group.group_name} value={group.group_name}>
                <Typography>
                  {group.group_name} (
                  {t(StringKeys.X_USERS, {
                    count: group.group_value?.length || 0,
                  })}
                  )
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Toolbar>
    </AppBar>
  );

  const renderDesktopView = () => (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: 240,
          boxSizing: 'border-box',
          top: 65,
          background: '#ffffff',
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',

          // Scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px', // scrollbar width
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a0a0a0',
          },

          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 transparent',
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#3e5371',
          boxShadow: 3,
          color: COLORS.WHITE,
          padding: '10px',
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 300,
              textAlign: 'left',
            }}
          >
            {t(StringKeys.GROUPS)}
          </Typography>

          <Tooltip title={t(StringKeys.ORGANIZATION_YOUR_TEAM_BASED_ON_GROUPS)}>
            <IconButton size="small" sx={{ color: COLORS.LIGHT_GRAY, mt: 0.5 }}>
              <Info />
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => setShowCreateNewGroupModal(true)}
        >
          {t(StringKeys.ADD_GROUP)} +
        </Button>
      </Box>

      <List sx={{ marginTop: '0px', paddingTop: '0px' }}>
        <ListItem
          component={Link}
          to={ALL_USERS_PATH}
          onClick={() => setActiveGroup(ALL_USERS_SLUG)}
          sx={{
            mt: 2,
            color: '#3A3B3C',
            borderRadius: 2,
            backgroundColor:
              activeGroup === ALL_USERS_SLUG
                ? ALL_USERS_SELECTED_BACKGROUND
                : '#ffffff',
            padding: '10px',
            '&:hover': {
              backgroundColor: COLORS.SIDEBAR_HOVER_BLUE,
            },
          }}
        >
          <ListItemText
            primary={t(StringKeys.ALL_USERS)}
            secondary={t(StringKeys.X_USERS, { count: allUsersCount })}
          />
        </ListItem>
        <Box
          sx={{
            height: '1px',
            backgroundColor: COLORS.MODAL_HEADER_GRAY,
            mx: 2,
            mt:2
          }}
        />
        {allGroups?.map((group) => (
          <ListItem
            key={group.group_name}
            component={Link}
            to={`/UserManagement/${encodeURIComponent(group.group_name)}`}
            onClick={() => setActiveGroup(group.group_name)}
            sx={{
              mt: 2,
              color: '#3A3B3C',
              borderRadius: 2,
              backgroundColor:
                activeGroup === group.group_name ? '#ededed' : '#ffffff',

              padding: '10px',
              '&:hover': {
                backgroundColor: COLORS.SIDEBAR_HOVER_BLUE,
              },
            }}
          >
            <ListItemText
              primary={group.group_name}
              secondary={t(StringKeys.X_USERS, {
                count: group.group_value?.length || 0,
              })}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default GroupManagementRouteBar;
